import { useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, Loader2, Search } from "lucide-react";
import type { AuditEntryDto, AuditPage } from "@ambulance/shared";
import { useApi } from "../api";
import { useMe } from "../hooks/useMe";
import { btnSecondary, ErrorBox, Forbidden, inputCls, Spinner } from "../components/ui";

const actorCls: Record<string, string> = { user: "bg-sky-100 text-sky-800", api_client: "bg-violet-100 text-violet-800", system: "bg-slate-200 text-slate-700" };

/** Super admin: who did what, when. Append-only on the server; read here with filters and paging. */
export function AuditLogPage() {
  const { me, isLoading } = useMe();
  const api = useApi();
  const [action, setAction] = useState("");
  const [entityType, setEntityType] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [applied, setApplied] = useState({ action: "", entityType: "", from: "", to: "" });
  const [open, setOpen] = useState<string | null>(null);

  const q = useInfiniteQuery({
    queryKey: ["audit-log", applied, api.token],
    enabled: Boolean(api.token),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ limit: "50" });
      if (pageParam) params.set("cursor", pageParam);
      if (applied.action) params.set("action", applied.action);
      if (applied.entityType) params.set("entityType", applied.entityType);
      if (applied.from) params.set("from", applied.from);
      if (applied.to) params.set("to", applied.to);
      return api.get<AuditPage>(`/api/v1/audit-log?${params}`);
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  if (isLoading) return <Spinner />;
  if (!me || me.role !== "super_admin") return <Forbidden />;
  const rows: AuditEntryDto[] = q.data?.pages.flatMap((p) => p.page) ?? [];

  return (
    <div>
      <h1 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2">Audit Log</h1>
      <p className="text-sm text-slate-600 mb-4">Every change and every external API call, with the actor and the time. Entries cannot be edited or deleted.</p>

      <form
        className="bg-white rounded-xl border border-slate-200 p-3 mb-4 grid grid-cols-2 md:grid-cols-5 gap-2 items-end"
        onSubmit={(e) => {
          e.preventDefault();
          setApplied({ action, entityType, from, to });
        }}
      >
        <label className="text-xs text-slate-600">Action starts with<input className={inputCls} value={action} onChange={(e) => setAction(e.target.value)} placeholder="checklist.submit" /></label>
        <label className="text-xs text-slate-600">Entity<select className={inputCls} value={entityType} onChange={(e) => setEntityType(e.target.value)}><option value="">All</option>{["checklist", "bagChecklist", "unit", "hospital", "ambulance", "unitCode", "user", "apiClient", "webhook", "api"].map((t) => <option key={t} value={t}>{t}</option>)}</select></label>
        <label className="text-xs text-slate-600">From<input type="date" className={inputCls} value={from} onChange={(e) => setFrom(e.target.value)} /></label>
        <label className="text-xs text-slate-600">To<input type="date" className={inputCls} value={to} onChange={(e) => setTo(e.target.value)} /></label>
        <button type="submit" className={btnSecondary}><Search className="w-4 h-4" /> Filter</button>
      </form>

      {q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorBox>{q.error.message}</ErrorBox>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-3 py-2 w-8"></th>
                  <th className="px-3 py-2">When</th>
                  <th className="px-3 py-2">Actor</th>
                  <th className="px-3 py-2">Action</th>
                  <th className="px-3 py-2">Entity</th>
                  <th className="px-3 py-2">Summary</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.length === 0 && <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-500">No entries match.</td></tr>}
                {rows.map((r) => {
                  const d = r.details;
                  const summary = r.action === "api.request" ? `${String(d.method)} ${String(d.path)} → ${String(d.status)}` : Object.entries(d).filter(([k]) => !["was"].includes(k)).slice(0, 3).map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : String(v)}`).join(" · ");
                  return (
                    <>
                      <tr key={r.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => setOpen(open === r.id ? null : r.id)}>
                        <td className="px-3 py-2 text-slate-400">{open === r.id ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-slate-700">{new Date(r.createdAt).toLocaleString()}</td>
                        <td className="px-3 py-2"><span className={`text-[10px] uppercase px-1.5 py-0.5 rounded mr-1 ${actorCls[r.actorType]}`}>{r.actorType === "api_client" ? "api" : r.actorType}</span>{r.actorName ?? r.actorId ?? "-"}</td>
                        <td className="px-3 py-2 font-mono text-xs">{r.action}</td>
                        <td className="px-3 py-2 text-slate-600">{r.entityType}{r.entityId ? <span className="text-slate-400 font-mono text-[10px]"> {r.entityId.slice(0, 8)}</span> : null}</td>
                        <td className="px-3 py-2 text-slate-600 max-w-md truncate">{summary}</td>
                      </tr>
                      {open === r.id && (
                        <tr key={`${r.id}-d`}>
                          <td></td>
                          <td colSpan={5} className="px-3 pb-3"><pre className="bg-slate-50 border border-slate-200 rounded p-2 text-xs overflow-x-auto">{JSON.stringify({ id: r.id, actorId: r.actorId, entityId: r.entityId, ip: r.ip, details: r.details }, null, 2)}</pre></td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
          {q.hasNextPage && (
            <div className="p-3 border-t border-slate-100 text-center">
              <button type="button" className={btnSecondary} onClick={() => void q.fetchNextPage()} disabled={q.isFetchingNextPage}>{q.isFetchingNextPage ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Load more</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
